import queryString from 'query-string';

// Configuração padrão de cache
const DEFAULT_CACHE_CONFIG = {
  // Tempo de expiração padrão em milissegundos (5 minutos)
  defaultExpiration: 5 * 60 * 1000,
  
  // Tamanho máximo do cache (em número de entradas)
  maxCacheSize: 100,
  
  // Habilitar armazenamento persistente (localStorage)
  enablePersistence: true,
  
  // Prefixo para as chaves no localStorage
  storageKeyPrefix: 'firebase_cache_',
  
  // Estratégia de cache para diferentes tipos de dados
  strategies: {
    // Dados que mudam com frequência
    dynamic: {
      expiration: 2 * 60 * 1000, // 2 minutos
      staleWhileRevalidate: true,
    },
    
    // Dados que mudam com pouca frequência
    static: {
      expiration: 30 * 60 * 1000, // 30 minutos
      staleWhileRevalidate: true,
    },
    
    // Dados de referência (ex: listas de categorias, serviços)
    reference: {
      expiration: 60 * 60 * 1000, // 60 minutos
      staleWhileRevalidate: true,
    }
  }
};

class CacheManager {
  constructor(config = {}) {
    // Mesclar configuração padrão com a configuração personalizada
    this.config = { ...DEFAULT_CACHE_CONFIG, ...config };
    
    // Cache em memória
    this.memoryCache = new Map();
    
    // Contador de hits/misses para análise de desempenho
    this.metrics = {
      hits: 0,
      misses: 0,
      staleHits: 0,
      storageHits: 0
    };
    
    // Registrar evento para sincronizar métricas com serviço externo
    this._setupMetricsSync();
    
    // Restaurar do localStorage se habilitado
    if (this.config.enablePersistence) {
      this._restoreFromStorage();
    }
    
    // Limpar entradas expiradas periodicamente (a cada 5 minutos)
    setInterval(() => this._cleanExpiredEntries(), 5 * 60 * 1000);
  }
  
  // Configurar sincronização de métricas com serviço externo
  _setupMetricsSync() {
    // URL do serviço de monitoramento de cache
    this.pythonServiceUrl = process.env.REACT_APP_CACHE_MONITOR_URL || 'http://localhost:5500';
    this.serviceAvailable = false;
    
    // Verificar disponibilidade do serviço
    this._checkServiceAvailability();
    
    // Configurar intervalo para verificar disponibilidade a cada 5 minutos
    setInterval(() => this._checkServiceAvailability(), 5 * 60 * 1000);
    
    // Sincronizar métricas periodicamente se o serviço estiver disponível
    setInterval(() => this._syncMetricsWithService(), 60 * 1000); // A cada minuto
  }
  
  // Verificar disponibilidade do serviço Python
  async _checkServiceAvailability() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3s timeout
      
      const response = await fetch(`${this.pythonServiceUrl}/`, {
        method: 'GET',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        this.serviceAvailable = true;
        console.log('Serviço de monitoramento de cache disponível');
      } else {
        this.serviceAvailable = false;
        console.warn('Serviço de monitoramento indisponível (resposta não-ok)');
      }
    } catch (error) {
      this.serviceAvailable = false;
      console.warn('Serviço de monitoramento indisponível:', error.name === 'AbortError' ? 'timeout' : error);
    }
  }
  
  // Sincronizar métricas com o serviço Python
  async _syncMetricsWithService() {
    if (!this.serviceAvailable) return;
    
    try {
      // Obter ID do salão do localStorage 
      const salonData = localStorage.getItem('salonData');
      if (!salonData) return;
      
      const salon = JSON.parse(salonData);
      const salonId = salon.id;
      
      if (!salonId) return;
      
      // Enviar métricas para o serviço
      const stats = this.getStats();
      
      const response = await fetch(`${this.pythonServiceUrl}/report/${salonId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(stats)
      });
      
      if (response.ok) {
        console.log('Métricas de cache sincronizadas com serviço externo');
      } else {
        console.warn('Falha ao sincronizar métricas com serviço externo:', await response.text());
      }
    } catch (error) {
      console.error('Erro ao sincronizar métricas com serviço externo:', error);
    }
  }
  
  /**
   * Gera uma chave de cache baseada no caminho e parâmetros
   * @param {string} path - Caminho do recurso (ex: 'salons/123/appointments')
   * @param {object} params - Parâmetros da consulta
   * @returns {string} - Chave de cache única
   */
  _generateCacheKey(path, params = {}) {
    // Ordenar os parâmetros para garantir consistência
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((acc, key) => {
        acc[key] = params[key];
        return acc;
      }, {});
    
    return `${path}:${queryString.stringify(sortedParams)}`;
  }
  
  /**
   * Verifica se o cache está expirado
   * @param {object} cacheEntry - Entrada de cache
   * @returns {boolean} - True se expirado
   */
  _isExpired(cacheEntry) {
    const now = Date.now();
    return now > cacheEntry.expiresAt;
  }
  
  /**
   * Verifica se o cache está "stale" (expirado mas ainda utilizável durante revalidação)
   * @param {object} cacheEntry - Entrada de cache
   * @returns {boolean} - True se stale
   */
  _isStale(cacheEntry) {
    if (!cacheEntry.strategy.staleWhileRevalidate) {
      return this._isExpired(cacheEntry);
    }
    
    const now = Date.now();
    // Considera stale se estiver dentro do período de grace
    // Período de grace é o dobro do tempo de expiração
    const graceExpiresAt = cacheEntry.expiresAt + cacheEntry.strategy.expiration;
    return now > cacheEntry.expiresAt && now <= graceExpiresAt;
  }
  
  /**
   * Limpa entradas expiradas do cache
   * @private
   */
  _cleanExpiredEntries() {
    const now = Date.now();
    
    // Limpar cache em memória
    for (const [key, entry] of this.memoryCache.entries()) {
      if (now > entry.expiresAt + entry.strategy.expiration) {
        this.memoryCache.delete(key);
      }
    }
    
    // Limpar localStorage se habilitado
    if (this.config.enablePersistence) {
      try {
        const keysToRemove = [];
        
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          
          if (key && key.startsWith(this.config.storageKeyPrefix)) {
            const value = JSON.parse(localStorage.getItem(key));
            
            if (now > value.expiresAt + value.strategy.expiration) {
              keysToRemove.push(key);
            }
          }
        }
        
        keysToRemove.forEach(key => localStorage.removeItem(key));
      } catch (error) {
        console.error('Erro ao limpar cache do localStorage:', error);
      }
    }
  }
  
  /**
   * Restaura cache do localStorage
   * @private
   */
  _restoreFromStorage() {
    try {
      const cacheEntries = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        
        if (key && key.startsWith(this.config.storageKeyPrefix)) {
          const storageKey = key.slice(this.config.storageKeyPrefix.length);
          const value = JSON.parse(localStorage.getItem(key));
          
          cacheEntries.push([storageKey, value]);
        }
      }
      
      // Ordenar por timestamp de expiração (mais recentes primeiro)
      cacheEntries.sort((a, b) => b[1].expiresAt - a[1].expiresAt);
      
      // Adicionar ao cache em memória (limitado pelo tamanho máximo)
      cacheEntries
        .slice(0, this.config.maxCacheSize)
        .forEach(([key, value]) => {
          this.memoryCache.set(key, value);
        });
      
      console.log(`Cache restaurado: ${this.memoryCache.size} entradas`);
    } catch (error) {
      console.error('Erro ao restaurar cache do localStorage:', error);
    }
  }
  
  /**
   * Salva uma entrada no cache persistente
   * @param {string} key - Chave do cache
   * @param {object} entry - Entrada de cache
   * @private
   */
  _saveToStorage(key, entry) {
    if (!this.config.enablePersistence) return;
    
    try {
      const storageKey = `${this.config.storageKeyPrefix}${key}`;
      localStorage.setItem(storageKey, JSON.stringify(entry));
    } catch (error) {
      console.error('Erro ao salvar cache no localStorage:', error);
    }
  }
  
  /**
   * Busca dados do cache
   * @param {string} path - Caminho do recurso
   * @param {object} params - Parâmetros da consulta
   * @param {string} strategyType - Tipo de estratégia ('dynamic', 'static', 'reference')
   * @returns {object|null} - Dados em cache ou null se não encontrado
   */
  get(path, params = {}, strategyType = 'dynamic') {
    this._cleanExpiredEntries();
    
    const key = this._generateCacheKey(path, params);
    let entry = this.memoryCache.get(key);
    
    // Se não encontrado em memória, tentar no localStorage
    if (!entry && this.config.enablePersistence) {
      try {
        const storageKey = `${this.config.storageKeyPrefix}${key}`;
        const storageData = localStorage.getItem(storageKey);
        
        if (storageData) {
          entry = JSON.parse(storageData);
          this.memoryCache.set(key, entry);
          this.metrics.storageHits++;
        }
      } catch (error) {
        console.error('Erro ao buscar cache do localStorage:', error);
      }
    }
    
    if (!entry) {
      this.metrics.misses++;
      return null;
    }
    
    // Verificar se está expirado
    if (this._isExpired(entry)) {
      if (entry.strategy.staleWhileRevalidate && !this._isStale(entry)) {
        // Ainda está no período de grace, retornar dados stale
        this.metrics.staleHits++;
        return { data: entry.data, isStale: true };
      } else {
        this.metrics.misses++;
        return null;
      }
    }
    
    this.metrics.hits++;
    return { data: entry.data, isStale: false };
  }
  
  /**
   * Armazena dados no cache
   * @param {string} path - Caminho do recurso
   * @param {object} params - Parâmetros da consulta
   * @param {any} data - Dados a serem armazenados
   * @param {string} strategyType - Tipo de estratégia ('dynamic', 'static', 'reference')
   */
  set(path, params = {}, data, strategyType = 'dynamic') {
    const key = this._generateCacheKey(path, params);
    const strategy = this.config.strategies[strategyType] || this.config.strategies.dynamic;
    
    const entry = {
      data,
      createdAt: Date.now(),
      expiresAt: Date.now() + strategy.expiration,
      strategy
    };
    
    // Verificar se o cache está cheio
    if (this.memoryCache.size >= this.config.maxCacheSize) {
      // Remover a entrada mais antiga
      let oldestKey = null;
      let oldestTime = Infinity;
      
      for (const [k, v] of this.memoryCache.entries()) {
        if (v.createdAt < oldestTime) {
          oldestTime = v.createdAt;
          oldestKey = k;
        }
      }
      
      if (oldestKey) {
        this.memoryCache.delete(oldestKey);
        
        if (this.config.enablePersistence) {
          try {
            const storageKey = `${this.config.storageKeyPrefix}${oldestKey}`;
            localStorage.removeItem(storageKey);
          } catch (error) {
            console.error('Erro ao remover cache do localStorage:', error);
          }
        }
      }
    }
    
    // Armazenar em memória
    this.memoryCache.set(key, entry);
    
    // Armazenar no localStorage se habilitado
    if (this.config.enablePersistence) {
      this._saveToStorage(key, entry);
    }
  }
  
  /**
   * Remove uma entrada específica do cache
   * @param {string} path - Caminho do recurso
   * @param {object} params - Parâmetros da consulta
   */
  invalidate(path, params = {}) {
    const key = this._generateCacheKey(path, params);
    
    // Remover da memória
    this.memoryCache.delete(key);
    
    // Remover do localStorage se habilitado
    if (this.config.enablePersistence) {
      try {
        const storageKey = `${this.config.storageKeyPrefix}${key}`;
        localStorage.removeItem(storageKey);
      } catch (error) {
        console.error('Erro ao remover cache do localStorage:', error);
      }
    }
  }
  
  /**
   * Invalida todas as entradas que começam com o caminho especificado
   * @param {string} pathPrefix - Prefixo do caminho
   */
  invalidateByPathPrefix(pathPrefix) {
    const keysToDelete = [];
    
    // Identificar chaves a serem removidas
    for (const [key] of this.memoryCache.entries()) {
      if (key.startsWith(pathPrefix)) {
        keysToDelete.push(key);
      }
    }
    
    // Remover da memória
    keysToDelete.forEach(key => {
      this.memoryCache.delete(key);
    });
    
    // Remover do localStorage se habilitado
    if (this.config.enablePersistence) {
      try {
        const storageKeys = [];
        
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          
          if (key && key.startsWith(this.config.storageKeyPrefix)) {
            const storageKey = key.slice(this.config.storageKeyPrefix.length);
            
            if (storageKey.startsWith(pathPrefix)) {
              storageKeys.push(key);
            }
          }
        }
        
        storageKeys.forEach(key => localStorage.removeItem(key));
      } catch (error) {
        console.error('Erro ao remover cache do localStorage:', error);
      }
    }
  }
  
  /**
   * Limpa todo o cache
   */
  clear() {
    // Limpar memória
    this.memoryCache.clear();
    
    // Limpar localStorage se habilitado
    if (this.config.enablePersistence) {
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          
          if (key && key.startsWith(this.config.storageKeyPrefix)) {
            localStorage.removeItem(key);
          }
        }
      } catch (error) {
        console.error('Erro ao limpar cache do localStorage:', error);
      }
    }
    
    // Reiniciar métricas
    this.metrics = {
      hits: 0,
      misses: 0,
      staleHits: 0,
      storageHits: 0
    };
  }
  
  /**
   * Obtém estatísticas do cache
   * @returns {object} - Estatísticas do cache
   */
  getStats() {
    const totalRequests = this.metrics.hits + this.metrics.misses;
    const hitRate = totalRequests > 0 ? this.metrics.hits / totalRequests : 0;
    
    return {
      size: this.memoryCache.size,
      hits: this.metrics.hits,
      misses: this.metrics.misses,
      staleHits: this.metrics.staleHits,
      storageHits: this.metrics.storageHits,
      hitRate: hitRate,
      effectiveHitRate: (this.metrics.hits + this.metrics.staleHits) / (totalRequests || 1)
    };
  }
}

// Exportar uma instância singleton
export const cacheManager = new CacheManager();

export default cacheManager; 